// src/util/pricing.js
import { mapIngredientsToCatalog } from './ingredientMapper';

// Proportional per-drink estimate (deprecated for totals; retained for compatibility)
export function calculateCocktailCostFromCatalog(cocktail, catalogs, options = {}) {
  const { storeId } = options;
  const mapped = mapIngredientsToCatalog(cocktail.ingredients || [], catalogs, { storeId });

  let total = 0;
  for (const line of mapped) {
    const {
      amountPerDrinkOz = 0,
      bottleSizeMl = 0,
      pricePerBottle = 0,
      mlPerOz = 29.5735,
      kind,
      capacityUnits = 0,
      capacityGrams = 0,
      amountUnits = 0,
      amountGrams = 0,
      custom = false,
      costPerDrink = 0,
    } = line;

    if (custom) {
      total += costPerDrink;
      continue;
    }

    if (bottleSizeMl <= 0 || pricePerBottle <= 0) continue;

    const perDrinkMl = amountPerDrinkOz * mlPerOz;
    const mlPerDollar = bottleSizeMl / pricePerBottle;
    const costPerDrinkCalc = perDrinkMl / mlPerDollar;
    total += costPerDrinkCalc;
  }
  return round2(total);
}

// Bottle-rounded cost for a single cocktail item and a given quantity of drinks (ALWAYS rounds up)
export function costForCocktailQuantityBottleRounded(cocktail, quantity, catalogs, options = {}) {
  const { storeId, servicePerCocktail = 0 } = options;
  const mapped = mapIngredientsToCatalog(cocktail.ingredients || [], catalogs, { storeId });

  let ingredientTotal = 0;
  for (const line of mapped) {
    const {
      amountPerDrinkOz = 0,
      bottleSizeMl = 0,
      pricePerBottle = 0,
      mlPerOz = 29.5735,
  kind = 'liquid',
  capacityUnits = 0,
  capacityGrams = 0,
  amountUnits = 0,
  amountGrams = 0,
      custom = false,
      costPerDrink = 0,
    } = line;

    if (custom) {
      // Custom per-drink cost: scale linearly by quantity
      ingredientTotal += (costPerDrink || 0) * quantity;
      continue;
    }

  // Liquids by ml
  if (kind === 'liquid' && bottleSizeMl > 0 && pricePerBottle > 0) {
      const perDrinkMl = (amountPerDrinkOz || 0) * mlPerOz;
      const totalMl = perDrinkMl * quantity;
      const bottles = Math.ceil(totalMl / bottleSizeMl);
      ingredientTotal += bottles * pricePerBottle;
      continue;
    }
  // Unit packs (e.g., cups, cans if catalog doesn’t expose ml)
  if (kind === 'unit' && capacityUnits > 0 && pricePerBottle > 0) {
      const totalUnits = (amountUnits || 0) * quantity;
      const packs = Math.ceil(totalUnits / capacityUnits);
      ingredientTotal += packs * pricePerBottle;
      continue;
    }
  // Weight packs (e.g., Mint 1 lb)
  if (kind === 'weight' && capacityGrams > 0 && pricePerBottle > 0) {
      const totalGrams = (amountGrams || 0) * quantity;
      const packs = Math.ceil(totalGrams / capacityGrams);
      ingredientTotal += packs * pricePerBottle;
      continue;
    }
  }

  const serviceTotal = Number(servicePerCocktail || 0) * quantity;
  return {
    ingredientTotal: round2(ingredientTotal),
    serviceTotal: round2(serviceTotal),
    total: round2(ingredientTotal + serviceTotal),
  };
}

// Legacy resolver using proportional per-drink estimate; prefer costForCocktailQuantityBottleRounded for totals
export function resolveCocktailPrice(cocktail, catalogs, opts = {}) {
  const ingredientCost = calculateCocktailCostFromCatalog(cocktail, catalogs, opts);
  const servicePerCocktail = Number(opts.servicePerCocktail || 0);
  return {
    ingredientCost,
    servicePerCocktail,
    perCocktailTotal: round2(ingredientCost + servicePerCocktail),
  };
}

export function computeOrderTotals({
  selectedCocktails = [],
  catalogs,
  guestCount = 0,
  drinksPerGuest = 0,
  baseServiceFee = 0,
  servicePerCocktail = 0,
  storeId,
}) {
  const totalDrinks = Math.max(0, guestCount * drinksPerGuest);

  if (!Array.isArray(selectedCocktails) || selectedCocktails.length === 0 || totalDrinks === 0) {
    const base = round2(baseServiceFee);
    return {
      counts: { guestCount, drinksPerGuest, totalDrinks },
      perCocktail: { ingredient: 0, service: round2(servicePerCocktail), total: round2(servicePerCocktail) },
      subtotals: { baseServiceFee: base, ingredient: 0, service: 0, drinkSubtotal: 0 },
      grandTotal: base,
      totalPerGuest: guestCount > 0 ? round2(base / guestCount) : 0,
    };
  }

  // Allocate drinks across cocktails using menuShare; then do bottle rounding per cocktail bucket
  const count = Math.max(1, selectedCocktails.length);
  let allocated = [];
  let allocatedSum = 0;
  for (const c of selectedCocktails) {
    const share = typeof c.menuShare === 'number' ? Math.max(0, c.menuShare) : (1 / count);
    const drinks = share * totalDrinks;
    const rounded = Math.max(0, Math.round(drinks));
    allocated.push({ cocktail: c, drinks: rounded });
    allocatedSum += rounded;
  }
  // Adjust rounding drift: ensure total equals totalDrinks by tweaking the largest shares first
  const diff = totalDrinks - allocatedSum;
  if (diff !== 0 && allocated.length > 0) {
    const sign = Math.sign(diff);
    for (let i = 0; i < Math.abs(diff); i++) {
      allocated[i % allocated.length].drinks += sign;
    }
  }

  let ingredientSubtotal = 0;
  let serviceSubtotal = 0;
  for (const { cocktail, drinks } of allocated) {
    if (drinks <= 0) continue;
    // Determine per-cocktail service fee dynamically from the cocktail if available
    const svc = Number.parseFloat(cocktail?.serviceFeePerCocktail ?? cocktail?.serviceFee ?? servicePerCocktail) || 0;
    const res = costForCocktailQuantityBottleRounded(cocktail, drinks, catalogs, { storeId, servicePerCocktail: svc });
    ingredientSubtotal += res.ingredientTotal;
    serviceSubtotal += Number(svc) * drinks;
  }

  const drinkSubtotal = round2(ingredientSubtotal + serviceSubtotal);
  const grandTotal = round2((baseServiceFee || 0) + drinkSubtotal);

  // Derive average per-cocktail costs for display
  const perDrinkIngredient = round2(ingredientSubtotal / totalDrinks);
  const perDrinkService = round2((serviceSubtotal) / totalDrinks);
  const perCocktailTotal = round2(perDrinkIngredient + perDrinkService);

  return {
    counts: { guestCount, drinksPerGuest, totalDrinks },
    perCocktail: {
      ingredient: perDrinkIngredient,
      service: perDrinkService,
      total: perCocktailTotal,
    },
    subtotals: {
      baseServiceFee: round2(baseServiceFee || 0),
      ingredient: round2(ingredientSubtotal),
      service: round2(serviceSubtotal),
      drinkSubtotal,
    },
    grandTotal,
    totalPerGuest: guestCount > 0 ? round2(grandTotal / guestCount) : 0,
  };
}

function round2(n) {
  return Math.round(n * 100) / 100;
}
